/**
 * Economics Panel — two sections, both driven by the auto-model:
 *   1. Best Use of This Water — every use case ranked at this parcel
 *      (capacity, water, yield, $/AF, revenue, runway).
 *   2. Revenue per Acre-Foot by Use Case — the same numbers as a bar chart.
 *
 * Revenue = annual yield × market price ÷ water demand, so every figure
 * reconciles. (The older single-use-case deep-dive — FMV, cost waterfall,
 * supply-driven pricing, margin erosion — was removed: it used a separate
 * revenue basis that contradicted these numbers.)
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/client';

interface WellPoint {
  well_name: string;
  latitude: number;
  longitude: number;
  total_depth_ft?: number | null;
  depth_to_water_ft?: number | null;
  ground_elevation_ft?: number | null;
}

interface Props {
  siteId: string;
  volumeAF: number;
  currentSatFt: number;
  declineRate: number;
  wellDepth: number;
  avgLiftFt: number;
  wellCount: number;
  aquifer: string;
  siteAcres: number;
  pumpGpm: number;
  predictionHorizons?: Record<string, Record<string, number>> | null;
  wells?: WellPoint[];
  storativity?: number;
  initialUseCase?: string;
  initialScale?: number;
}

// Static reference list — shown only while the live matrix is still loading.
const REVENUE_PER_AF = [
  { use_case: 'Data Center', revenue_per_af: 8929 },
  { use_case: 'Cattle', revenue_per_af: 5224 },
  { use_case: 'Municipal', revenue_per_af: 4464 },
  { use_case: 'Corn', revenue_per_af: 285 },
];

// Compact yield formatting (e.g. 146.0M, 512K, 8,000).
function fmtYield(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return n.toLocaleString();
}

// Tooltip that shows HOW each $/AF bar is calculated.
function RevPerAfTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-mono-950 border border-mono-700 rounded p-2 text-[11px] max-w-[240px]">
      <div className="text-mono-200 font-semibold">{d.use_case}</div>
      <div className="text-cyan-400 font-semibold">${Number(d.revenue_per_af).toLocaleString()}/AF</div>
      {d.price_source === 'capacity_value' ? (
        <div className="text-mono-500 mt-0.5">Capacity/water value (yield not sold as a commodity).</div>
      ) : d.unit_price != null ? (
        <div className="text-mono-400 mt-0.5">
          = {fmtYield(d.yield_per_af)} {d.yield_unit}/AF × ${d.unit_price}/{d.yield_unit}
          {d.price_source === 'usda_live' ? ' (USDA live)' : ' (market est.)'}
        </div>
      ) : null}
    </div>
  );
}

export function EconomicsPanel({ siteId, volumeAF, currentSatFt, declineRate, wellDepth, aquifer, siteAcres }: Props) {
  // Auto-model: every use case at this parcel's acreage + water budget.
  const [matrix, setMatrix] = useState<any>(null);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [horizonYears, setHorizonYears] = useState(30);

  const runMatrix = useCallback(async () => {
    if (!siteId || !volumeAF || !siteAcres) return;
    setMatrixLoading(true);
    try {
      const resp = await apiClient.post('/phreatic/calculator/economics-auto-model-all', {
        site_id: siteId,
        site_area_acres: siteAcres,
        volume_available_af: volumeAF,
        current_sat_thickness_ft: currentSatFt,
        projected_decline_ft_yr: declineRate,
        well_depth_ft: wellDepth,
        planning_horizon_years: horizonYears,
        aquifer,
      });
      setMatrix(resp.data);
    } catch {
      setMatrix(null);
    } finally {
      setMatrixLoading(false);
    }
  }, [siteId, siteAcres, volumeAF, currentSatFt, declineRate, wellDepth, horizonYears, aquifer]);

  useEffect(() => { runMatrix(); }, [runMatrix]);

  // Revenue-per-AF bars, driven by the SAME computation as the table, ranked.
  const revPerAfData = matrix?.scenarios?.length
    ? [...matrix.scenarios]
        .map((s: any) => ({
          use_case: s.name,
          revenue_per_af: s.revenue_per_af,
          yield_per_af: s.yield_per_af,
          yield_unit: s.yield_unit,
          unit_price: s.unit_price,
          price_source: s.price_source,
        }))
        .sort((a, b) => b.revenue_per_af - a.revenue_per_af)
    : REVENUE_PER_AF;

  return (
    <div className="space-y-3">
      {/* ── Best Use of This Water — every use case at THIS parcel ── */}
      <div className="bg-mono-900 border border-cyan-500/20 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider">
              Best Use of This Water — All Scenarios at {siteAcres.toLocaleString()} acres
            </h3>
            <div className="text-[10px] text-mono-500 mt-0.5">
              Crops sized to your acreage; water-intensive uses sized to what {volumeAF.toLocaleString()} AF sustains over the horizon. Ranked by annual revenue.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] text-mono-500 uppercase">Horizon</label>
            <select value={horizonYears} onChange={e => setHorizonYears(Number(e.target.value))}
              className="px-2 py-1 text-[11px] bg-mono-800 border border-mono-700 rounded text-mono-200">
              {[10, 20, 30, 50].map(y => <option key={y} value={y}>{y} yr</option>)}
            </select>
            {matrixLoading && <ArrowPathIcon className="w-3.5 h-3.5 text-cyan-400 animate-spin" />}
          </div>
        </div>

        {matrix && matrix.floyd_applicable === false && (
          <div className="bg-blue-500/5 border border-blue-500/20 rounded p-2 mb-3 text-[11px] text-mono-300">
            <span className="text-blue-300 font-semibold">Floyd Factor N/A.</span>{' '}
            {matrix.aquifer_geology?.aquifer} is a {matrix.aquifer_geology?.confinement} {matrix.aquifer_geology?.lithology} aquifer —
            the 40ft saturated-thickness cutoff doesn't apply, so full volume is recoverable and runways reflect linear depletion.
          </div>
        )}

        {matrix?.best && (
          <div className="bg-cyan-500/5 border border-cyan-500/20 rounded p-2 mb-3 text-xs text-mono-300">
            <span className="text-cyan-300 font-semibold">Top scenario:</span>{' '}
            <span className="text-mono-100 font-semibold">{matrix.best.name}</span> —{' '}
            {matrix.best.capacity.toLocaleString()} {matrix.best.unit} producing{' '}
            <span className="text-cyan-400 font-semibold">${(matrix.best.annual_revenue / 1e6).toFixed(2)}M/yr</span>{' '}
            at ${matrix.best.revenue_per_af.toLocaleString()}/AF, {matrix.best.years_remaining_adjusted}-yr runway.
          </div>
        )}

        {matrix?.scenarios?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-mono-400 border-b border-mono-800">
                  <th className="text-left py-1 px-2">#</th>
                  <th className="text-left py-1 px-2">Use Case</th>
                  <th className="text-right py-1 px-2">Capacity</th>
                  <th className="text-right py-1 px-2">Water (AF/yr)</th>
                  <th className="text-right py-1 px-2">Avg GPM</th>
                  <th className="text-right py-1 px-2">Annual Yield</th>
                  <th className="text-right py-1 px-2">$/AF</th>
                  <th className="text-right py-1 px-2">Annual Revenue</th>
                  <th className="text-right py-1 px-2">Runway</th>
                  <th className="text-center py-1 px-2">Limited By</th>
                </tr>
              </thead>
              <tbody>
                {matrix.scenarios.map((s: any) => (
                  <tr key={s.use_case} className="border-b border-mono-800/50 text-mono-300">
                    <td className="py-1 px-2 text-mono-500">{s.rank}</td>
                    <td className="py-1 px-2 font-medium text-mono-200">
                      {s.name}
                      <span className="text-[9px] text-mono-500 ml-1">{s.group}</span>
                    </td>
                    <td className="text-right py-1 px-2">{s.capacity.toLocaleString()} <span className="text-mono-500 text-[10px]">{s.unit}</span></td>
                    <td className="text-right py-1 px-2">{s.annual_demand_af.toLocaleString()}</td>
                    <td className="text-right py-1 px-2">{s.avg_gpm.toLocaleString()}</td>
                    <td className="text-right py-1 px-2 text-cyan-400">{fmtYield(s.annual_yield)} <span className="text-mono-500 text-[10px]">{s.yield_unit}</span></td>
                    <td className="text-right py-1 px-2 text-cyan-400">${s.revenue_per_af.toLocaleString()}</td>
                    <td className="text-right py-1 px-2 text-cyan-400 font-semibold">${(s.annual_revenue / 1e6).toFixed(2)}M</td>
                    <td className="text-right py-1 px-2">{s.years_remaining_adjusted} yr</td>
                    <td className="text-center py-1 px-2">
                      <span className="px-1.5 py-0.5 rounded text-[9px] bg-cyan-500/15 text-cyan-300">
                        {s.capacity_limited_by}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] text-mono-500 mt-2">
              <span className="text-cyan-300">Land</span> = capped by your acreage · <span className="text-cyan-300">Water</span> = capped by sustainable supply.
            </div>
          </div>
        ) : (
          !matrixLoading && <div className="text-mono-500 text-xs text-center py-4">No scenarios available for this parcel.</div>
        )}
      </div>

      {/* ── Revenue per AF chart ── */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider mb-1">
          Revenue per Acre-Foot by Use Case
        </h3>
        <p className="text-[10px] text-mono-500 mb-3">
          Revenue/AF = annual yield × market price ÷ water demand (AF/yr). Crops use live USDA prices;
          livestock/ag use market estimates. Data center &amp; oil/gas use capacity-based water value.
        </p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={revPerAfData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
            <XAxis type="number" tick={{ fill: '#888', fontSize: 10 }} tickFormatter={(v: number) => `$${v.toLocaleString()}`} />
            <YAxis type="category" dataKey="use_case" tick={{ fill: '#ccc', fontSize: 10 }} width={90} interval={0} />
            <Tooltip content={<RevPerAfTooltip />} />
            <Bar dataKey="revenue_per_af" fill="#22d3ee" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* ── Methodology — how acreage, water, price, and units build the numbers ── */}
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
        <h3 className="text-xs font-semibold text-mono-300 uppercase tracking-wider mb-3">
          How these numbers are calculated
        </h3>
        <div className="space-y-2 text-[11px] text-mono-400 leading-relaxed">
          <div>
            <span className="text-cyan-400 font-mono">Capacity</span> — crops: your parcel acreage ·
            livestock: acreage × stocking rate (feedyard 120, dairy 60, poultry 8,000, swine 400 per acre) ·
            industrial &amp; municipal: what the water sustains = (volume ÷ horizon) ÷ water-per-unit.
          </div>
          <div>
            <span className="text-cyan-400 font-mono">Water (AF/yr)</span> = capacity × water-per-unit
            (e.g. corn 2.0 AF/acre, cattle 0.0134 AF/head, data center 560 AF/MW).
          </div>
          <div>
            <span className="text-cyan-400 font-mono">Annual Yield</span> = capacity × yield-per-unit
            (e.g. corn 200 bu/acre, cattle 1,900 lb beef/head, data center 7,450 MWh/MW).
          </div>
          <div>
            <span className="text-cyan-400 font-mono">Revenue</span> = yield × market price — crops use
            live USDA prices, livestock/ag use market estimates, and data center &amp; oil/gas use a
            capacity-based water value (their yield isn't sold as a commodity).
          </div>
          <div>
            <span className="text-cyan-400 font-mono">$/AF (return on water)</span> = revenue ÷ water demand
            — what each acre-foot of groundwater earns.
          </div>
          <div>
            <span className="text-cyan-400 font-mono">Runway</span> = recoverable volume ÷ water demand,
            Floyd-adjusted (the 40 ft pump-efficiency cutoff applies to unconfined aquifers only).
          </div>
        </div>
        <div className="text-[10px] text-mono-600 mt-3 pt-2 border-t border-mono-800">
          Chain: acreage / water budget → capacity → water demand + yield → revenue → $/AF + runway.
        </div>
      </div>
    </div>
  );
}
